import React, { useEffect, useState } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { useAppStateContext } from "../../state/AppContextProvider";
import FingerprintIcon from "@mui/icons-material/Fingerprint";
import {
  getCurrentOrchestration,
  OrchestrationType,
} from "../../common/models";
import NinjaActiveIcon from "../../assets/icons-ninja-active.png";

const agentStepMap: Record<string, number> = {
  NegotiatorAgent: 0,
  POVAgent: 1,
  CaviaarAgent: 1, // Treated same as POVAgent for stepper
  RiskAssessmentAgent: 2,
  SituationAssessmentAgent: 2, // Treated same as RiskAssessmentAgent for stepper
  NegotiationOneAgent: 3,
  SimulationAgent: 4,
  // ExperienceTransformerAgent and CaviaarAgent are ignored
};

interface ChevronStepProps {
  label: string;
  active: boolean;
}

const ChevronStep: React.FC<ChevronStepProps> = ({ label, active }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const borderColor = active
    ? "#e50c20"
    : isDark
      ? "rgba(64, 64, 64, 1)"
      : "rgba(229, 12, 32, 0.2)";
  const bgColor = active
    ? "#e50c20"
    : isDark
      ? "rgba(38, 38, 38, 1)"
      : "rgba(248, 249, 250, 1)";

  return (
    <Box
      sx={{
        position: "relative",
        flex: 1,
        height: 40,
        mx: -0.2,
      }}
    >
      {/* SVG for proper chevron border */}
      <svg
        width="100%"
        height="100%"
        viewBox="0 0 100 40"
        preserveAspectRatio="none"
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          pointerEvents: "none",
        }}
      >
        {/* Fill */}
        <polygon points="0,0 90,0 100,20 90,40 0,40 10,20" fill={bgColor} />
        {/* Border stroke */}
        <polygon
          points="0,0 90,0 100,20 90,40 0,40 10,20"
          fill="none"
          stroke={borderColor}
          strokeWidth="2"
          vectorEffect="non-scaling-stroke"
        />
      </svg>

      {/* Text content */}
      <Box
        sx={{
          position: "relative",
          px: 1,
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          zIndex: 1,
        }}
      >
        <Typography
          variant="body2"
          sx={{
            // Active state uses white text for both modes; inactive uses muted gray
            color: active
              ? "#ffffff"
              : isDark
                ? "rgba(148, 163, 184, 1)"
                : "rgba(100, 116, 139, 1)",
            fontWeight: active ? 600 : "normal",
            fontSize: {
              xs: "45%", // mobile
              sm: "12px", // tablet and up
              md: "14px", // desktop and up
            },
            lineHeight: {
              xs: "5px", // mobile
              sm: "1.2", // tablet and up
            },
            transition: "color 0.3s ease, font-weight 0.3s ease",
            mx: "3px",
            textAlign: "center",
            wordWrap: "break-word",
            whiteSpace: "normal",
            overflowWrap: "break-word",
          }}
        >
          {active ? label : ""}
        </Typography>
      </Box>
    </Box>
  );
};

interface ChevronProgressProps {
  currentAgent?: string | null;
}

const ChevronProgress: React.FC<ChevronProgressProps> = ({ currentAgent }) => {
  // Determine conversation flow from global app state (preferred) or
  // fall back to agent name. This lets the UI show the correct stepper
  // immediately when a user selects the flow (DECEPTION vs NEGOTIATION).
  const appState = useAppStateContext();

  let activeStep = 0;

  const maxStep = appState.state.maxStep;
  const currentOrchestrationType = appState?.state?.currentOrchestrationType;

  // Determine labels based on orchestrationType

  const chevronLabels =
    currentOrchestrationType === OrchestrationType.ContinueNegotiation
      ? ["Intel", "Caviaar", "Situation Assessment", "One Sheet", "Sim"]
      : ["Intel", "POV", "Risk Assessment", "One Sheet", "Sim"];

  // If we're running the Deception flow, replace the full chevron stepper
  // with a single centered chevron-like badge that uses the fingerprint icon
  // and the label "Deception analysis" so the UI clearly reflects a
  // different process flow.
  if (currentOrchestrationType === OrchestrationType.DeceptionAnalysis) {
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";
    // Note: invert icon color per request previously handled on line 93
    const iconColor = isDark ? "#000000" : "#ffffff";
    const textColor = isDark ? "#ffffff" : "#000000";

    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          background: "transparent",
          px: 2,
          py: 1,
          borderRadius: 2,
          border: "2px solid #e50c20",
          transition: "transform 0.2s ease, box-shadow 0.3s ease",
          transform: "perspective(600px) rotateX(25deg)",
          transformStyle: "preserve-3d",
        }}
      >
        <FingerprintIcon
          sx={{ fontSize: { xs: 26, sm: 36 }, color: "#e50c20" }}
        />
        <Typography
          variant="caption"
          sx={{
            mt: 0.5,
            color: "#e50c20",
            fontWeight: 700,
            fontSize: { xs: "12px", sm: "14px" },
          }}
        >
          Deception analysis
        </Typography>
      </Box>
    );
  }

  // If we're running the Deception flow, replace the full chevron stepper
  // with a single centered chevron-like badge that uses the fingerprint icon
  // and the label "Deception analysis" so the UI clearly reflects a
  // different process flow.
  if (currentOrchestrationType === OrchestrationType.AdhocNegotiation) {
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";
    // Note: invert icon color per request previously handled on line 93
    const iconColor = isDark ? "#000000" : "#ffffff";
    const textColor = isDark ? "#ffffff" : "#000000";

    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          background: "transparent",
          px: 2,
          py: 1,
          borderRadius: 2,
          border: "2px solid #e50c20",
          transition: "transform 0.2s ease, box-shadow 0.3s ease",
          transform: "perspective(600px) rotateX(25deg)",
          transformStyle: "preserve-3d",
        }}
      >
        <Box
          component="img"
          src={NinjaActiveIcon}
          alt="Ninja"
          width={30}
          height={30}
        />{" "}
        <Typography
          variant="caption"
          sx={{
            mt: 0.5,
            color: "#e50c20",
            fontWeight: 700,
            fontSize: { xs: "12px", sm: "14px" },
          }}
        >
          Advanced Negotiation
        </Typography>
        {currentAgent && (
          <Typography
            variant="caption"
            sx={{
              mt: 1,
              color: "#e50c20",
              fontWeight: 500,
              fontSize: { xs: "10px", sm: "12px" },
            }}
          >
            {currentAgent}
          </Typography>
        )}
      </Box>
    );
  }

  useEffect(() => {
    //look at the agent stack to determine the highest step reached
    if (
      appState &&
      appState?.state?.currentOrchState?.orchestrations &&
      appState.state.currentOrchState.orchestrations.length > 0
    ) {


      //determine which orchestration to look at based on whether first negotiation prep is completed
      const currentOrch = getCurrentOrchestration(
        appState.state.currentOrchState,
      );

     // currentOrch?.currentAgent

      //loop through agents in the orchestration to find the highest step
      currentOrch?.agentStack.forEach((agentName) => {
        const agentStep = agentStepMap.hasOwnProperty(agentName)
          ? agentStepMap[agentName]
          : 0;

        if (agentStep > activeStep) {
          activeStep = agentStep;
        }
      });
    }

    // Reset and recalculate max step only if activeStep is greater
    if (activeStep > maxStep) {
      appState.setMaxStep(activeStep);
    }
  }, [activeStep, appState, currentOrchestrationType]);

  return (
    <Box
      display="flex"
      width="95%"
      maxWidth={{ xs: "100%", sm: 720 }}
      alignItems="center"
      bgcolor="background.default"
      mx="auto"
      boxSizing="border-box"
    >
      {chevronLabels.map((label, idx) => (
        <ChevronStep key={idx} label={label} active={idx <= maxStep} />
      ))}
    </Box>
  );
};

export default ChevronProgress;
